import {
  Component,
  ChangeDetectionStrategy,
  signal,
  computed,
  input,
  output,
  inject,
  effect,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { LabelModule } from '@progress/kendo-angular-label';
import { CyflareAutocompleteComponent } from '../../../../../../../../../../core/components/cyflare-autocomplete/cyflare-autocomplete.component';
import { ServiceTypeConfig } from '../../../../models/service-type-mapping.models';

/**
 * Reusable row component for Type / Sub-Type / Item selection.
 *
 * Layout:
 * - Row 1: Type and Sub-Type side-by-side (Sub-Type cascades from Type)
 * - Row 2: Item on its own line (independent, filtered by board)
 *
 * Key behaviors:
 * - All fields are optional
 * - Sub-Type disabled until Type selected; clearing Type clears Sub-Type
 * - Item is independent of Type/Sub-Type — filtered by boardId
 * - Emits config changes immediately on selection
 */
@Component({
  selector: 'app-type-subtype-item-row',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LabelModule, CyflareAutocompleteComponent],
  templateUrl: './type-subtype-item-row.component.html',
  styleUrls: ['./type-subtype-item-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TypeSubtypeItemRowComponent {
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);

  // Inputs
  organizationId = input<number | null>(null);
  boardId = input<number | null>(null);
  config = input<ServiceTypeConfig | null>(null);
  disabled = input<boolean>(false);

  // Output - emits current config state on any change
  configChanged = output<ServiceTypeConfig>();

  // Form groups for each autocomplete
  typeForm: FormGroup = this.fb.group({ selectedType: [null] });
  subTypeForm: FormGroup = this.fb.group({ selectedSubType: [null] });
  itemForm: FormGroup = this.fb.group({ selectedItem: [null] });

  // Selected items for autocomplete default values
  selectedTypeItem = signal<{ id: number; display_text: string } | null>(null);
  selectedSubTypeItem = signal<{ id: number; display_text: string } | null>(null);
  selectedItemItem = signal<{ id: number; display_text: string } | null>(null);

  // Disabled state for Sub-Type (cascades from Type)
  subTypeDisabled = signal<boolean>(true);

  // Selected Type ID for Sub-Type filtering
  selectedTypeId = signal<number | null>(null);

  // Computed API URLs — include boardId/typeId in path (empty string disables the autocomplete fetch)
  typeApiUrl = computed(() => {
    const board = this.boardId();
    return board
      ? `v1/ticketintegration/administrative/organizations/{orgId}/boards/${board}/types`
      : '';
  });

  subTypeApiUrl = computed(() => {
    const board = this.boardId();
    return board
      ? `v1/ticketintegration/administrative/organizations/{orgId}/boards/${board}/subtypes`
      : '';
  });

  itemApiUrl = computed(() => {
    const board = this.boardId();
    return board
      ? `v1/ticketintegration/administrative/organizations/{orgId}/boards/${board}/items`
      : '';
  });

  constructor() {
    this.initConfigEffect();
  }

  /**
   * Effect to sync input config with internal state
   */
  private initConfigEffect(): void {
    effect(() => {
      const inputConfig = this.config();
      if (inputConfig) {
        // Set type
        this.selectedTypeItem.set(inputConfig.type);
        if (inputConfig.type) {
          this.selectedTypeId.set(inputConfig.type.id);
          this.subTypeDisabled.set(false);
        }

        // Set sub-type
        this.selectedSubTypeItem.set(inputConfig.subtype);

        // Set item (independent of type/sub-type)
        this.selectedItemItem.set(inputConfig.item);

        // Force CD so autocomplete [defaultItem] inputs pick up the new signal values immediately
        this.cdr.detectChanges();
      }
    }, { allowSignalWrites: true });
  }

  /**
   * Extra query params to filter sub-types by selected type
   */
  subTypeExtraParams = computed(() => {
    const typeId = this.selectedTypeId();
    return typeId ? `type_id=${typeId}` : '';
  });

  /**
   * Handle type selection
   */
  onTypeSelected(item: { id: number; display_text: string } | null): void {
    this.selectedTypeItem.set(item);
    if (item?.id) {
      this.selectedTypeId.set(item.id);
      this.subTypeDisabled.set(false);
    } else {
      this.selectedTypeId.set(null);
      this.subTypeDisabled.set(true);
    }
    // Reset Sub-Type only (Item is independent)
    this.clearSubType();
    this.emitCurrentConfig();
  }

  /**
   * Handle type cleared
   */
  onTypeCleared(): void {
    this.selectedTypeItem.set(null);
    this.selectedTypeId.set(null);
    this.subTypeDisabled.set(true);
    this.clearSubType();
    this.emitCurrentConfig();
  }

  /**
   * Handle sub-type selection
   */
  onSubTypeSelected(item: { id: number; display_text: string } | null): void {
    this.selectedSubTypeItem.set(item);
    this.emitCurrentConfig();
  }

  /**
   * Handle sub-type cleared
   */
  onSubTypeCleared(): void {
    this.selectedSubTypeItem.set(null);
    this.emitCurrentConfig();
  }

  /**
   * Handle item selection
   */
  onItemSelected(item: { id: number; display_text: string } | null): void {
    this.selectedItemItem.set(item);
    this.emitCurrentConfig();
  }

  /**
   * Handle item cleared
   */
  onItemCleared(): void {
    this.selectedItemItem.set(null);
    this.emitCurrentConfig();
  }

  /**
   * Clear sub-type field (when Type is cleared/changed)
   */
  private clearSubType(): void {
    this.subTypeForm.reset();
    this.selectedSubTypeItem.set(null);
    this.cdr.markForCheck();
  }

  /**
   * Emit current configuration state
   */
  private emitCurrentConfig(): void {
    const config: ServiceTypeConfig = {
      type: this.selectedTypeItem(),
      subtype: this.selectedSubTypeItem(),
      item: this.selectedItemItem(),
    };

    this.configChanged.emit(config);
    this.cdr.markForCheck();
  }

  /**
   * Check if row is effectively disabled
   */
  isRowDisabled(): boolean {
    return this.disabled();
  }
}
